import React from 'react'
import {connect} from 'react-redux'
import styled from 'styled-components'
import {injectIntl} from 'react-intl'
import {Email} from '@mui/icons-material'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Slide from '@mui/material/Slide'
import EmailTemplate from 'components/EmailTemplate'
import ErrorBoundary from 'components/ErrorBoundary'
import DraggableList from 'components/DraggableList'
import {getEmailTemplates} from 'utils/utils'
import {
	cloneEmailTemplate,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    sendEmailTemplate,
    getEmailTemplate,
    resetEmailTemplate,
    getSignatures,
    setEmailTemplatesOrder
} from 'modules/email/actions'
import {fieldtype} from 'utils/enums/fieldtype'

const Container = styled.div`
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    position: fixed;
    align-items: center;
    justify-content: center;
`

const Backdrop = styled.div`
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: fixed;
    background-color: rgba(0, 0, 0, 0.5);
`

const Dialog = styled.div`
    width: calc(100% - 64px);
    max-width: 960px;
    display: flex;
    max-height: calc(100% - 64px);
    flex-direction: column;
    margin: 32px;
    position: relative;
    overflow-y: auto;
    box-shadow: 0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12);
    border-radius: 4px;
    color: #4a4a4a;
    background-color: #fff;
`

const Icon = styled(Email)`
	margin-right: 10px;
	font-size: 18px;
`

const List = styled(MenuItem)`
	font-size: 14px;
	line-height: normal;

	&:hover {
		color: #ffc300;
        background-color: rgba(22, 22, 27, 1);
	}
`

class EmailContent extends React.Component {
    constructor(props) {
        super(props)

        this.templates = getEmailTemplates(this.props.entity)
        this.emission = null
        this.template = null
        this.order = []

		if (this.props.duplicate) {
			this.template = {...this.props.duplicate}
			this.emission = this.props.emissions.find(item => item.id === this.props.duplicate.emissionPath.join(','))
            this.props.resetEmailTemplate()
		}

        this.state = {
            open: this.props.duplicate ? true : false
        }
    }

    componentDidUpdate(props) {
        if (this.props.template) {
            this.template = {...this.props.template}
            this.props.resetEmailTemplate()
            this.handleOpenDialog()
        }

        if (!this.props.test && this.props.successes && !this.props.errors) {
            this.handleCloseDialog()
        }
    }

    handleOpenDialog = () => {
        this.setState({
            open: true
        }, () => this.props.getSignatures(this.props.application))
    }

    handleCloseDialog = () => {
        this.emission = null
        this.template = null

        this.setState({
            open: false
        }, () => this.props.handleClose())
    }

    selectTemplate = (e) => {
        const id = Number(e.currentTarget.getAttribute('data-list-item-id'))
        const template = this.templates.find(item => item.id === id)
        this.emission = this.props.emissions.find(item => item.id === template.emissionPath.join(','))

        this.props.getEmailTemplate(id)
    }

    selectEmission = (e) => {
        this.emission = this.props.emissions.find(item => item.id === e.currentTarget.getAttribute('data-id'))
        this.handleOpenDialog()
    }

    sendEmailTemplate = (template, save, test) => {
        this.props.sendEmailTemplate(template, this.props.documents, save, test)
    }

    onReorder = (order, id) => {
        const index = this.order.findIndex(item => item.emission === id)
        this.order[index].templates = [...order]
        const data = this.order.reduce((acc, item) => acc.concat(item.templates), [])
        this.props.setEmailTemplatesOrder(this.props.entity, data)

    }
    
    render() {
        const {intl, emissions} = this.props
        const {open} = this.state

        return (
            <React.Fragment>
                <Menu
                    anchorEl={this.props.anchorEl}
                    open={!open && !!this.props.anchorEl}
                    onClose={this.props.handleClose}
                    disablePortal={true}
                    PaperProps={{
						sx: {
							background: '#3c3d4c',
							color: 'rgba(255, 255, 255, .7)',
							borderRadius: 0
						}
					}}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    {emissions.map(item => {
                        const templates = this.templates.filter(template => template.emissionPath.join(',') === item.id)
                        this.order.push({emission: item.id, templates: templates.map(el => el.id)})
                        
                        return <div key={item.id}>
                            <List data-id={item.id}
                                onClick={this.selectEmission} disableRipple
                            >
                                <Icon />
                                {intl.formatMessage({id: "sidebar.send_to"}, {name: '', to: item.pathName})}
                            </List>
                            <DraggableList
                                data={templates}
                                onClick={this.selectTemplate}
                                onReorder={(order) => this.onReorder(order, item.id)}
                            />
                        </div>
                    })}
                </Menu>
                {open &&
                    <Container>
                        <Backdrop onClick={this.handleCloseDialog} />
                        <Slide in={open}>
                            <Dialog>
                                <ErrorBoundary message='An error occurred while parsing email template. Please, contact to support...'>
                                    <EmailTemplate
                                        test={this.props.test}
                                        sending={!!this.props.documents.length}
                                        entity={this.props.entity}
                                        name={this.props.name}
                                        handleClose={this.handleCloseDialog}
                                        handleReset={this.props.resetEmailTemplate}
                                        emission={this.emission}
                                        template={this.template}
                                        createEmailTemplate={this.props.createEmailTemplate}
                                        deleteEmailTemplate={this.props.deleteEmailTemplate}
                                        updateEmailTemplate={this.props.updateEmailTemplate}
										cloneEmailTemplate={this.props.cloneEmailTemplate}
                                        sendEmailTemplate={this.sendEmailTemplate}
                                        successes={this.props.successes}
                                        errors={this.props.errors}
                                        fields={this.props.entities.find(item =>
                                            item.id === this.props.entity).fields.filter(field =>
                                                field.uiType === fieldtype.check)}
                                    />
                                </ErrorBoundary>
                            </Dialog>
                        </Slide>
                    </Container>
                }
            </React.Fragment>
        )
    }
}

const mapStateToProps = ({email, entities, applications}) => ({
    template: email.template,
    entities: entities.entities,
    application: applications.current,
    test: email.test
})

export default connect(mapStateToProps, {
	cloneEmailTemplate,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    sendEmailTemplate,
    getEmailTemplate,
    resetEmailTemplate,
    getSignatures,
    setEmailTemplatesOrder
})(injectIntl(EmailContent))
